import { Box, Grid, Skeleton, Text } from '@chakra-ui/react';

import {
  type CompanySentiment,
  SENTIMENT_LABELS,
  type SentimentScores,
} from '../hooks/useSentimentComparison';

const COMPARE_SENTIMENT_KEYS: Array<keyof SentimentScores> = [
  'overall',
  'career_growth',
  'compensation',
  'benefits',
  'work_life_balance',
  'culture',
  'leadership',
  'job_security',
  'diversity_and_inclusion',
  'remote_work',
];

const getSentimentColor = (value: number): string => {
  if (value >= 0.8) return 'green.500';
  if (value >= 0.7) return 'green.400';
  if (value >= 0.5) return 'orange.500';
  return 'red.500';
};

interface CompanyCompareSentimentProps {
  sentiments: CompanySentiment[];
  companyOrder: number[];
  isLoading: boolean;
}

export const CompanyCompareSentiment = ({
  sentiments,
  companyOrder,
  isLoading,
}: CompanyCompareSentimentProps) => {
  const sentimentMap = new Map(sentiments.map((s) => [s.companyId, s]));
  const colCount = companyOrder.length;

  if (isLoading) {
    return <Skeleton height="200px" borderRadius="md" />;
  }

  if (sentiments.length === 0) {
    return null;
  }

  return (
    <Box>
      <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={2}>
        Sentiment Comparison
      </Text>

      {COMPARE_SENTIMENT_KEYS.map((key) => {
        const values = companyOrder.map((rcid) => {
          const s = sentimentMap.get(rcid);
          return s?.scores[key] ?? null;
        });

        // Find best value for highlighting
        const validValues = values.filter((v): v is number => v !== null);
        const bestVal =
          validValues.length > 0 ? Math.max(...validValues) : null;

        return (
          <Grid
            key={key}
            templateColumns={`140px repeat(${colCount}, 1fr)`}
            gap={2}
            py={1.5}
            borderBottom="1px solid"
            borderColor="gray.50"
          >
            <Text fontSize="xs" color="gray.500" alignSelf="center">
              {SENTIMENT_LABELS[key]}
            </Text>
            {values.map((v, i) => (
              <Box key={i} textAlign="center">
                <Text
                  fontSize="sm"
                  fontWeight={
                    v === bestVal && validValues.length > 1 ? 'bold' : 'normal'
                  }
                  color={v !== null ? getSentimentColor(v) : 'gray.300'}
                >
                  {v !== null ? `${(v * 5).toFixed(1)}/5` : '\u2014'}
                </Text>
              </Box>
            ))}
          </Grid>
        );
      })}
    </Box>
  );
};
